import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import Button from "devextreme-react/button";
import notify from "devextreme/ui/notify";
import DataGrid, {
    Column,
    ColumnChooser,
    ColumnChooserSearch,
    Export,
    FilterPanel,
    FilterRow,
    HeaderFilter,
    LoadPanel,
    Lookup,
    Pager,
    Paging,
    Selection,
    Sorting,
    StateStoring,
    Toolbar as GridToolbar,
    Item as GridToolbarItem,
} from "devextreme-react/data-grid";
import { LoadPanel as GridLoadPanel } from "devextreme-react/load-panel";
import { confirm } from "devextreme/ui/dialog";
import { createStore } from "devextreme-aspnet-data-nojquery";
import DataSource from "devextreme/data/data_source";
import data from "../../assets/apiRoutes.json";
import { GridLayout } from "../../common/GridLayout";
import GridLayoutManager from "../../gridLayoutCustomization/GridLayoutManager";
import { MESSAGES } from "../../common/Messages";
import { CustomerAccountStatus, CustomerPortalAccountRoles } from "../../const/constants";
import CreateCustomerAccount from "./CreateCustomerAccount";

// Customer Account Administration (T20260914.0001).
//
// The screen a CSR Manager uses to create and administer customer portal accounts. Account creation
// happens only from here, inside an authenticated session - the BR forbids any public, unauthenticated
// registration endpoint on WM Web (Security Requirement 1).

const gridKey = "b7e4c1a9-3f52-4d68-8a07-19c5e2f6b43d";
const allowedPageSizes = [10, 20, 50, 500, 1000, "all"];

// FR11's Account Status filter. A fixed list rather than one built from the rows, so a status that
// nobody currently holds can still be filtered on.
const accountStatusOptions = [
    { value: CustomerAccountStatus.Invited, text: CustomerAccountStatus.Invited },
    { value: CustomerAccountStatus.Active, text: CustomerAccountStatus.Active },
    { value: CustomerAccountStatus.Disabled, text: CustomerAccountStatus.Disabled },
    { value: CustomerAccountStatus.DisabledInactivity, text: CustomerAccountStatus.DisabledInactivity },
    { value: CustomerAccountStatus.DisabledCreditHold, text: CustomerAccountStatus.DisabledCreditHold },
];

// The identity endpoint returns every user in the store, internal ones included. This screen
// administers customer portal accounts only, so the list is narrowed to their roles. Compared without
// regard to case or surrounding space, because a role name is free text in the identity store.
const normalizedPortalRoles = CustomerPortalAccountRoles.map((role) => role.trim().toLowerCase());

const isCustomerPortalAccount = (account) =>
    normalizedPortalRoles.includes((account?.userRoleName || "").trim().toLowerCase());

// Accounts created from this screen carry their own status. Accounts that predate it have none, so for
// those the only state they actually have is the identity lockout, and the column falls back to it
// rather than showing a blank.
const resolveAccountStatus = (row) => {
    if (row && row.accountStatus) return row.accountStatus;
    if (!row?.lockoutEnd) return MESSAGES.ACCOUNT_STATUS_ACTIVE;
    return new Date(row.lockoutEnd) > new Date()
        ? MESSAGES.ACCOUNT_STATUS_DISABLED
        : MESSAGES.ACCOUNT_STATUS_ACTIVE;
};

const CustomerAccountAdministration = (props) => {
    const accessToken = props.oidc.user.access_token;
    const dataGrid = useRef(null);

    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [currentLayout, setCurrentLayout] = useState("Default");
    const [showCreate, setShowCreate] = useState(false);
    const [busy, setBusy] = useState(false);

    // FR11 asks for Customer Name, which the identity store does not hold - it keeps only the customer
    // code, in the identityId claim. The names come from the ABECAS customer master and are matched up
    // by code here.
    const [customerNames, setCustomerNames] = useState({});

    const loadState = useCallback(
        () => GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout),
        [accessToken, currentLayout]
    );
    const saveState = useCallback(
        (state) => { GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout); },
        [accessToken, currentLayout]
    );

    const loadAccounts = useCallback(() => {
        const grid = dataGrid.current ? dataGrid.current.instance() : null;
        if (grid) {
            grid.beginCustomLoading(MESSAGES.LOADING);
        }
        axios
            .get(`${window._env_.REACT_APP_AUTH_URL}/${data.idp.getUsers}`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            })
            .then((res) => {
                if (grid) {
                    grid.endCustomLoading();
                }
                const allUsers = Array.isArray(res.data) ? res.data : [];
                setAccounts(allUsers.filter(isCustomerPortalAccount));
            })
            .catch(() => {
                if (grid) {
                    grid.endCustomLoading();
                }
                notify(MESSAGES.FETCH_ERROR, "error", 3000);
            });
    }, [accessToken]);

    // Loaded once. Only used to put a name beside a code, so a customer that cannot be resolved simply
    // leaves the column blank rather than holding up the list.
    const loadCustomerNames = useCallback(() => {
        const store = createStore({
            key: "id",
            loadUrl: `${window._env_.REACT_APP_APIS}/${data.Common.GetLookup}`,
            loadParams: { module: "id", lookupType: "Customer", showLookupGrid: true },
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = { Authorization: `Bearer ${accessToken}` };
            },
        });

        new DataSource({ store, paginate: false })
            .load()
            .then((rows) => {
                const map = {};
                (rows || []).forEach((row) => {
                    const code = (row.identityid || "").trim();
                    if (code) map[code.toLowerCase()] = (row.customerName || "").trim();
                });
                setCustomerNames(map);
            })
            .catch(() => {
                // Names are a convenience; the list is still usable with codes alone.
            });
    }, [accessToken]);

    useEffect(() => {
        loadAccounts();
        loadCustomerNames();
    }, [loadAccounts, loadCustomerNames]);

    const onSelectionChanged = (e) => {
        setSelectedAccount(e?.selectedRowsData?.length === 1 ? e.selectedRowsData[0] : null);
    };

    const resolveCustomerName = (row) => {
        const code = (row?.identityId?.value || "").trim().toLowerCase();
        return code ? customerNames[code] || "" : "";
    };

    // Both actions apply to accounts awaiting a first login only. The server enforces that too - this
    // just keeps the buttons honest. Read through the same resolver the Account Status column uses, so
    // a row that reads Invited on screen is always one these buttons are live for, and compared without
    // regard to case.
    const isAwaitingFirstLogin =
        !!selectedAccount &&
        resolveAccountStatus(selectedAccount).trim().toLowerCase() ===
            CustomerAccountStatus.Invited.toLowerCase();

    const actionsEnabled = isAwaitingFirstLogin && !busy;

    // FR14. Disable applies to an Active account, Enable to a Disabled one - an account still awaiting
    // its first login belongs to Resend and Cancel instead, so neither applies to it.
    const selectedStatus = selectedAccount ? resolveAccountStatus(selectedAccount).trim().toLowerCase() : "";
    const canDisable = !busy && selectedStatus === CustomerAccountStatus.Active.toLowerCase();
    // Any disabled state, not only the manual one - an account retired for inactivity has to have a way
    // back, and FR14 gives that to the CSR Manager.
    const canEnable = !busy && selectedStatus.startsWith(CustomerAccountStatus.Disabled.toLowerCase());

    // Goes to the WebAPI, not the identity server: this is the one action that has to leave an audit
    // row, and that lives in the company database.
    const setAccountStatus = (enable, confirmText) => {
        const account = selectedAccount;
        if (!account) return;

        confirm(`<i>${confirmText}</i>`, "Confirm").then((confirmed) => {
            if (!confirmed) return;

            setBusy(true);
            axios
                .post(
                    `${window._env_.REACT_APP_APIS}/${data.idp.SetCustomerAccountStatus}`,
                    { UserId: account.id, Enable: enable },
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                )
                .then((res) => {
                    setBusy(false);
                    const result = res.data || {};
                    notify(result.Message || MESSAGES.ERROR, result.Success ? "success" : "error", 5000);
                    setSelectedAccount(null);
                    loadAccounts();
                })
                .catch((err) => {
                    setBusy(false);
                    notify(err?.response?.data?.Message || MESSAGES.ERROR, "error", 5000);
                });
        });
    };

    const runAccountAction = (route, confirmText) => {
        const account = selectedAccount;
        if (!account) return;

        confirm(`<i>${confirmText}</i>`, "Confirm").then((confirmed) => {
            if (!confirmed) return;

            setBusy(true);
            axios
                .post(
                    `${window._env_.REACT_APP_AUTH_URL}/${route}`,
                    { userId: account.id },
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                )
                .then((res) => {
                    setBusy(false);
                    const result = res.data || {};
                    notify(result.message || MESSAGES.ERROR, result.success ? "success" : "error", 5000);
                    setSelectedAccount(null);
                    loadAccounts();
                })
                .catch((err) => {
                    setBusy(false);
                    notify(err?.response?.data?.message || MESSAGES.ERROR, "error", 5000);
                });
        });
    };

    return (
        <div className="h-100" style={{ overflowY: "hidden", marginTop: "10px" }}>
            {/* Resend issues a token and sends an email, Cancel deletes an account - both take long
                enough to need covering, and neither should be triggered twice. */}
            <GridLoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                visible={busy}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
            />
            <DataGrid
                onExporting={(e) => {
                    if (window.handleDevExtremeDataGridExport) {
                        window.handleDevExtremeDataGridExport(e);
                    }
                }}
                ref={dataGrid}
                dataSource={accounts}
                keyExpr="id"
                showBorders={true}
                hoverStateEnabled={true}
                rowAlternationEnabled={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                showRowLines={true}
                showColumnLines={false}
                onSelectionChanged={onSelectionChanged}
            >
                <StateStoring
                    enabled={true}
                    type="custom"
                    customLoad={loadState}
                    storageKey={gridKey}
                    customSave={saveState}
                />
                <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                    <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: "Search column" }} />
                </ColumnChooser>
                <LoadPanel enabled={true} />
                <Sorting mode="multiple" />
                <FilterRow visible={true} />
                <FilterPanel visible={true} />
                <HeaderFilter visible={true} />
                <Export enabled={true} allowExportSelectedData={true} />
                <Selection mode="single" allowSelectAll={false} />
                <Paging defaultPageSize={10} />
                <Pager
                    visible={true}
                    showPageSizeSelector={true}
                    allowedPageSizes={allowedPageSizes}
                    showNavigationButtons={true}
                    showInfo={true}
                />

                {/* FR11's column set, in the order the requirement lists them. */}
                <Column dataField="identityId.value" caption="Customer ID" />
                {/* A calculated column has no dataField, so it needs an explicit name: the grid keys
                    its filter and its saved layout state on one, and refuses to render otherwise. */}
                <Column
                    name="customerName"
                    caption="Customer Name"
                    dataType="string"
                    calculateCellValue={resolveCustomerName}
                />
                <Column dataField="userName" caption="User Name" />
                <Column dataField="email" caption="Email Address" />
                <Column
                    name="accountStatus"
                    caption="Account Status"
                    dataType="string"
                    allowFiltering={true}
                    calculateCellValue={resolveAccountStatus}
                >
                    {/* FR11: filterable by Account Status, from a known list rather than whatever the
                        current page happens to contain. */}
                    <Lookup dataSource={accountStatusOptions} valueExpr="value" displayExpr="text" />
                </Column>
                <Column
                    dataField="invitationDateTime"
                    caption="Invitation Date"
                    dataType="datetime"
                    format="MM/dd/yyyy hh:mm aa"
                />
                <Column
                    dataField="activationDateTime"
                    caption="Activation Date"
                    dataType="datetime"
                    format="MM/dd/yyyy hh:mm aa"
                />
                <Column
                    dataField="lastLoginDateTime"
                    caption="Last Login Date"
                    dataType="datetime"
                    format="MM/dd/yyyy hh:mm aa"
                />
                <Column dataField="userRoleName" caption="Role" />

                <GridToolbar>
                    <GridToolbarItem location="before">
                        <Button text="Create" type="default" onClick={() => setShowCreate(true)} />
                    </GridToolbarItem>
                    {/* Keyed on their own enabled state. A toolbar item is rendered through the grid's
                        template, which does not always pick up a changed prop on its own - without the
                        key these stay disabled however many rows you select. */}
                    <GridToolbarItem location="before">
                        <Button
                            key={`resend-${actionsEnabled}`}
                            text="Resend Invitation"
                            disabled={!actionsEnabled}
                            onClick={() =>
                                runAccountAction(
                                    data.idp.ResendCustomerAccountInvitation,
                                    MESSAGES.CUSTOMER_ACCOUNT_RESEND_CONFIRM
                                )
                            }
                        />
                    </GridToolbarItem>
                    <GridToolbarItem location="before">
                        <Button
                            key={`cancel-${actionsEnabled}`}
                            text="Cancel Invitation"
                            disabled={!actionsEnabled}
                            onClick={() =>
                                runAccountAction(
                                    data.idp.CancelCustomerAccountInvitation,
                                    MESSAGES.CUSTOMER_ACCOUNT_CANCEL_CONFIRM
                                )
                            }
                        />
                    </GridToolbarItem>
                    <GridToolbarItem location="before">
                        <Button
                            key={`disable-${canDisable}`}
                            text="Disable"
                            disabled={!canDisable}
                            onClick={() => setAccountStatus(false, MESSAGES.CUSTOMER_ACCOUNT_DISABLE_CONFIRM)}
                        />
                    </GridToolbarItem>
                    <GridToolbarItem location="before">
                        <Button
                            key={`enable-${canEnable}`}
                            text="Enable"
                            disabled={!canEnable}
                            onClick={() => setAccountStatus(true, MESSAGES.CUSTOMER_ACCOUNT_ENABLE_CONFIRM)}
                        />
                    </GridToolbarItem>
                    <GridToolbarItem location="after">
                        <GridLayoutManager
                            gridKey={gridKey}
                            dataGrid={dataGrid}
                            setCurrentLayout={setCurrentLayout}
                        />
                    </GridToolbarItem>
                    <GridToolbarItem name="exportButton" />
                    <GridToolbarItem name="columnChooserButton" />
                </GridToolbar>
            </DataGrid>

            <CreateCustomerAccount
                visible={showCreate}
                onClose={() => setShowCreate(false)}
                onCreated={() => {
                    setShowCreate(false);
                    loadAccounts();
                }}
            />
        </div>
    );
};

function mapStateToProps(state) {
    return {
        ReceiptReducer: state.ReceiptReducer,
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(CustomerAccountAdministration);
